import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import {
  CheckCircle2,
  XCircle,
  ChevronRight,
  Menu,
  X,
  ArrowUp,
  Package,
  Search,
  Building2,
  Mail,
  ShieldCheck,
  Languages,
  Play,
  ArrowRight,
  Globe,
  Boxes,
  Workflow,
  Banknote,
  Sparkles,
  Volume2,
  VolumeX,
  CalendarDays,
  Info,
  Cpu,
  Layers,
} from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import axios from "axios";
import { STARLINK_ENABLED } from "../lib/features";
import LeadForm from "../components/LeadForm";
import { SERVICE_AVAILABILITY, captureCampaignParams, selectService, track } from "../lib/funnel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WHATSAPP_NUMBER = "31645282452";
const CONTACT_EMAIL = "adm@globaltechxperts.nl";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3eb1f7e1-02cf-4acb-8ee2-746b00b03901/artifacts/kuo8ppes_Logo%20Global%20tech%20500x500%20FT.png";

const STOCK_VIDEOS = [
  "https://customer-assets.emergentagent.com/job_iphoneshop-1/artifacts/vg6l92b6_WhatsApp%20Video%202026-02-11%20at%2023.16.23.mp4",
  "https://customer-assets.emergentagent.com/job_iphoneshop-1/artifacts/10dyp1rt_WhatsApp%20Video%202026-02-11%20at%2023.16.23%20%281%29.mp4",
  "https://customer-assets.emergentagent.com/job_iphoneshop-1/artifacts/bzzv630d_WhatsApp%20Video%202026-02-11%20at%2023.16.23%20%282%29.mp4",
];

// Service explainer videos (real, hosted on customer-assets)
const HERO_VIDEOS = {
  es: "https://customer-assets.emergentagent.com/job_3eb1f7e1-02cf-4acb-8ee2-746b00b03901/artifacts/qlfn9nuk_avatar-shot-20260513-703d3d4f.mp4",
  en: "https://customer-assets.emergentagent.com/job_3eb1f7e1-02cf-4acb-8ee2-746b00b03901/artifacts/ajxqfwru_avatar-shot-20260513-154ded42.mp4",
};

const SERVICE_VIDEOS = {
  import: {
    es: "https://customer-assets.emergentagent.com/job_3eb1f7e1-02cf-4acb-8ee2-746b00b03901/artifacts/nrd04xbn_GTX_IMPORT_ESPA%C3%91OL%20okok.mp4",
    en: "https://customer-assets.emergentagent.com/job_3eb1f7e1-02cf-4acb-8ee2-746b00b03901/artifacts/0kicr7gt_V%C3%ADdeo_de_avatar%20IMPort%20English%20ok%20ok.mp4",
  },
  buy: {
    es: "https://customer-assets.emergentagent.com/job_3eb1f7e1-02cf-4acb-8ee2-746b00b03901/artifacts/aboxpla0_GTX%20buy%20for%20you%20Espa%C3%B1ol.mp4",
    en: "https://customer-assets.emergentagent.com/job_3eb1f7e1-02cf-4acb-8ee2-746b00b03901/artifacts/58d9i1df_buy%20for%20you%20English.mp4",
  },
  europe: {
    en: "https://customer-assets.emergentagent.com/job_3eb1f7e1-02cf-4acb-8ee2-746b00b03901/artifacts/k5rs6z74_GTX_Europe_Entry_English.mp4",
  },
};

const getWA = (msg) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;

const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL;

function useCalendly() {
  useEffect(() => {
    if (document.getElementById("calendly-widget-js")) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://assets.calendly.com/assets/external/widget.css";
    document.head.appendChild(link);
    const script = document.createElement("script");
    script.id = "calendly-widget-js";
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  return (e) => {
    e.preventDefault();
    if (window.Calendly) {
      window.Calendly.initPopupWidget({ url: CALENDLY_URL });
    } else {
      window.open(CALENDLY_URL, "_blank", "noopener,noreferrer");
    }
  };
}

function WhatsAppIcon({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.21, 0.47, 0.32, 0.98] },
};

// ============================================================
// Header
// ============================================================
function Header() {
  const { t, language, toggleLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { href: "#servicios", label: t("nav.services") },
    { href: "#comparativa", label: t("nav.compare") },
    { href: "#sobre-gtx", label: t("nav.about") },
    { href: "#faq", label: t("nav.faq") },
    { href: "#contacto", label: t("nav.contact") },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-300 shadow-[0_1px_0_rgba(15,23,42,0.04)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <a href="#top" className="flex items-center gap-3 group" data-testid="header-logo">
            <div className="relative">
              <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-full bg-white p-[2px] ring-2 ring-blue-500/50 group-hover:ring-blue-400 overflow-hidden transition-[transform,box-shadow] duration-300 group-hover:scale-105 shadow-[0_6px_28px_-6px_rgba(59,130,246,0.6)]">
                <img src={LOGO_URL} alt="Global Tech Xperts" className="w-full h-full object-cover rounded-full scale-[1.12]" />
              </div>
              <div className="absolute inset-0 rounded-full bg-blue-500/40 blur-xl -z-10 opacity-60 group-hover:opacity-100 transition-opacity" />
            </div>
            <div className="leading-tight hidden sm:block">
              <div className="font-bold text-[15px] text-slate-900 tracking-tight">Global Tech Xperts</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold mono">
                B2B · Apple · Samsung
              </div>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                data-testid={`nav-${item.href.slice(1)}`}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <HeaderWhatsApp />
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-2.5 sm:px-3 py-2 text-xs font-bold text-slate-900 bg-white hover:bg-slate-50 border border-slate-300 hover:border-slate-400 rounded-md transition-all uppercase tracking-wider"
              data-testid="language-toggle"
              aria-label={language === "es" ? "Switch to English" : "Cambiar a Español"}
              title={language === "es" ? "Switch to English" : "Cambiar a Español"}
            >
              <FlagIcon code={language === "es" ? "ES" : "EN"} />
              <span className="text-slate-900">{language.toUpperCase()}</span>
              <span className="text-slate-500">/</span>
              <span className="text-slate-400">{language === "es" ? "EN" : "ES"}</span>
            </button>
            <a href="#contacto" className="hidden sm:block" data-testid="header-quote-btn" onClick={(e) => { e.preventDefault(); selectService("import", { source: "header" }); }}>
              <Button className="bg-blue-600 hover:bg-blue-500 text-white rounded-md px-4 h-9 text-sm font-semibold transition-all shadow-md shadow-blue-600/25">
                {t("nav.quote")}
              </Button>
            </a>
            <button
              className="lg:hidden p-2 text-slate-800"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="lg:hidden border-t border-slate-300 py-3"
          >
            <nav className="flex flex-col gap-1">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className="px-4 py-3 text-sm font-medium text-slate-800 hover:bg-slate-100 rounded-md"
                  onClick={() => setOpen(false)}
                >
                  {item.label}
                </a>
              ))}
              <button
                onClick={toggleLanguage}
                className="mx-4 mt-2 flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-800 border border-slate-300 rounded-md self-start"
              >
                <Languages className="w-3.5 h-3.5" />
                {language.toUpperCase()}
              </button>
            </nav>
          </motion.div>
        )}
      </div>
    </header>
  );
}

// ============================================================
// Hero
// ============================================================
function Hero() {
  const { t, language } = useLanguage();
  const heroVideoSrc = HERO_VIDEOS[language] || HERO_VIDEOS.es;
  const openCalendly = useCalendly();

  return (
    <section id="top" className="relative pt-32 lg:pt-44 pb-24 lg:pb-32 overflow-hidden noise">
      {/* mesh gradient bg */}
      <div className="absolute inset-0 mesh-hero pointer-events-none" />
      <div className="absolute inset-0 grid-pattern pointer-events-none" />

      {/* aurora orbs */}
      <motion.div
        className="aurora-orb"
        style={{
          top: "10%",
          right: "-10%",
          width: 480,
          height: 480,
          background: "radial-gradient(circle, #3B82F6 0%, transparent 70%)",
        }}
        animate={{ scale: [1, 1.1, 1], opacity: [0.4, 0.55, 0.4] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="aurora-orb"
        style={{
          bottom: "0%",
          left: "-5%",
          width: 380,
          height: 380,
          background: "radial-gradient(circle, #6366F1 0%, transparent 70%)",
        }}
        animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-8 space-y-7 lg:pt-8">
            <motion.div {...fadeUp}>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-[11px] font-semibold text-slate-800 uppercase tracking-[0.18em] mono">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-blue-500" />
                </span>
                {t("hero.badge")}
              </span>
            </motion.div>

            <motion.h1
              {...fadeUp}
              transition={{ duration: 0.7, delay: 0.05, ease: [0.21, 0.47, 0.32, 0.98] }}
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] leading-[1.08]"
              data-testid="hero-title"
            >
              <span className="text-gradient">{t("hero.titleLine1")}</span>
              <br />
              <span className="text-gradient-accent">{t("hero.titleLine2")}</span>
            </motion.h1>

            <motion.p
              {...fadeUp}
              transition={{ duration: 0.7, delay: 0.12 }}
              className="text-base sm:text-lg lg:text-xl text-slate-600 max-w-2xl leading-relaxed"
              data-testid="hero-subtitle"
            >
              {t("hero.subtitle")}
            </motion.p>

            <motion.p
              {...fadeUp}
              transition={{ duration: 0.7, delay: 0.14 }}
              className="text-sm sm:text-base text-slate-800 font-medium max-w-2xl flex items-start gap-2"
              data-testid="hero-support"
            >
              <Cpu className="w-4 h-4 mt-1 text-blue-600 shrink-0" />
              <span>{t("hero.support")}</span>
            </motion.p>

            <motion.div
              {...fadeUp}
              transition={{ duration: 0.7, delay: 0.16 }}
              className="flex items-start gap-2.5 max-w-2xl rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-xs sm:text-sm text-amber-900/85 leading-relaxed"
              data-testid="hero-vat-note"
            >
              <Info className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
              <span>{t("hero.vatNote")}</span>
            </motion.div>

            <motion.div
              {...fadeUp}
              transition={{ duration: 0.7, delay: 0.18 }}
              className="flex flex-col sm:flex-row sm:flex-wrap gap-3 pt-2"
            >
              <a href="#contacto" data-testid="hero-quote-btn" onClick={(e) => { e.preventDefault(); selectService("import", { source: "hero" }); }} className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-blue-600 hover:bg-blue-500 text-white rounded-lg px-8 py-6 text-base font-semibold gap-2 transition-all shadow-lg shadow-blue-600/25 hover:shadow-blue-500/40 active:scale-[0.98] hover:-translate-y-0.5"
                >
                  {t("hero.cta1")}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </a>
              <a href={CALENDLY_URL} onClick={(e) => { track.calendlyClick("hero"); openCalendly(e); }} data-testid="hero-calendly-btn" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white border border-slate-900 rounded-lg px-8 py-6 text-base font-medium gap-2 transition-all hover:-translate-y-0.5"
                >
                  <CalendarDays className="w-4 h-4" />
                  {t("hero.ctaCalendly")}
                </Button>
              </a>
            </motion.div>

            <motion.div {...fadeUp} transition={{ duration: 0.7, delay: 0.2 }}>
              <a
                href="#servicios"
                data-testid="hero-services-btn"
                className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-800 underline underline-offset-4 decoration-blue-300 hover:decoration-blue-500 transition-colors"
              >
                {t("hero.cta3")}
                <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>

            <motion.div
              {...fadeUp}
              transition={{ duration: 0.7, delay: 0.22 }}
              className="grid sm:grid-cols-3 gap-3 pt-4 max-w-3xl"
              data-testid="hero-service-buttons"
            >
              {[
                { href: "#servicio-import", tag: t("services.importTag"), title: t("services.importTitle"), icon: Package, id: "import" },
                { href: "#servicio-europe-entry", tag: t("services.europeTag"), title: t("services.europeTitle"), icon: Globe, id: "europe" },
                { href: "#servicio-buy", tag: t("services.buyTag"), title: t("services.buyTitle"), icon: Search, id: "buy" },
              ].map(({ href, tag, title, icon: Icon, id }) => (
                <a
                  key={id}
                  href={href}
                  data-testid={`hero-service-btn-${id}`}
                  className="group flex items-center gap-3 px-4 py-3.5 rounded-xl glass border border-slate-300 hover:border-blue-300 hover:bg-blue-50 transition-[background-color,border-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-500/10"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 group-hover:bg-blue-500 group-hover:text-slate-900 transition-colors duration-300">
                    <Icon className="w-5 h-5" />
                  </span>
                  <span className="flex flex-col min-w-0">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 mono">{tag}</span>
                    <span className="text-sm font-bold text-slate-900 tracking-tight truncate">{title}</span>
                  </span>
                  <ChevronRight className="w-4 h-4 ml-auto text-slate-600 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-[color,transform] duration-300" />
                </a>
              ))}
            </motion.div>

          </div>

          {/* Operation summary panel */}
          <motion.aside
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="lg:col-span-4 lg:pt-10"
            data-testid="hero-summary-panel"
          >
            <div className="rounded-2xl border border-slate-300 bg-white p-6 md:p-7 shadow-[0_24px_48px_-24px_rgba(15,23,42,0.18)]">
              <div className="flex items-center justify-between mb-5">
                <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mono">GTX IMPORT</span>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-700 mono">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  {language === "es" ? "Disponible" : "Available"}
                </span>
              </div>
              <ul className="space-y-3">
                {[t("hero.kpi1"), t("hero.kpi2"), t("hero.kpi3"), t("hero.kpi4")].map((k, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-800">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                    <span>{k}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-5 border-t border-slate-300 grid grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mono mb-1">{language === "es" ? "Sede" : "HQ"}</div>
                  <div className="text-sm font-medium text-slate-900">Rotterdam, NL</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mono mb-1">{language === "es" ? "Respuesta" : "Response"}</div>
                  <div className="text-sm font-medium text-slate-900">{language === "es" ? "Evaluación previa" : "Prior assessment"}</div>
                </div>
              </div>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Operativa GTX strip — 3 bullets (moved from hero)
// ============================================================
function OperativaStrip() {
  const { t } = useLanguage();
  const items = [
    { title: t("hero.bullet1Title"), desc: t("hero.bullet1Desc"), icon: Workflow },
    { title: t("hero.bullet2Title"), desc: t("hero.bullet2Desc"), icon: Boxes },
    { title: t("hero.bullet3Title"), desc: t("hero.bullet3Desc"), icon: Banknote },
  ];

  return (
    <section className="relative py-12 lg:py-16 border-t border-slate-300" data-testid="operativa-strip">
      <div className="absolute inset-0 mesh-soft pointer-events-none opacity-50" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex items-center gap-2 mb-6 text-[11px] font-bold text-blue-600 uppercase tracking-[0.22em] mono">
          <ShieldCheck className="w-3.5 h-3.5" />
          {t("operativa.title")}
        </div>
        <div className="grid sm:grid-cols-3 gap-4 lg:gap-5">
          {items.map((b, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="glass-strong rounded-2xl p-6 border border-slate-300"
            >
              <b.icon className="w-5 h-5 text-blue-600 mb-3" strokeWidth={1.5} />
              <div className="font-semibold text-[15px] text-slate-900 mb-1.5">{b.title}</div>
              <div className="text-sm text-slate-600 leading-relaxed">{b.desc}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Chooser — "¿Qué necesita tu empresa?"
// ============================================================
function ChooserSection() {
  const { t } = useLanguage();
  const icons = { import: Package, europe: Building2, buy: Search };
  const anchors = { import: "#servicio-import", europe: "#servicio-europe-entry", buy: "#servicio-buy" };

  return (
    <section id="elige-servicio" className="py-20 lg:py-28 relative section-band">
      <div className="absolute inset-0 mesh-soft pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div {...fadeUp} className="max-w-3xl mb-12">
          <span className="text-[11px] font-bold tracking-[0.22em] text-blue-600 uppercase mono mb-3 block">
            {t("chooser.tag")}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-gradient mb-4 leading-[1.08] tracking-tight" data-testid="chooser-title">
            {t("chooser.title")}
          </h2>
          <p className="text-base md:text-lg text-slate-600">{t("chooser.subtitle")}</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-4">
          {t("chooser.cases").map((c, i) => {
            const Icon = icons[c.id];
            const available = SERVICE_AVAILABILITY[c.id] === "available";
            return (
              <motion.a
                key={c.id}
                href={anchors[c.id]}
                onClick={() => track.serviceViewed(c.id)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`group relative card-gradient-border rounded-2xl p-7 block overflow-hidden ${c.id === "import" ? "ring-2 ring-blue-500/70 shadow-xl shadow-blue-500/10" : ""}`}
                data-testid={`chooser-case-${c.id}`}
              >
                <div className={`absolute top-0 inset-x-0 h-1 ${available ? "bg-gradient-to-r from-blue-600 to-blue-400" : "bg-gradient-to-r from-amber-500 to-amber-300"}`} />
                <div className="flex items-start justify-between mb-5">
                  <Icon className="w-9 h-9 text-blue-600" strokeWidth={1.3} />
                  <span
                    className={`text-[10px] font-bold uppercase tracking-[0.18em] mono px-2.5 py-1 rounded-full border ${
                      available ? "text-emerald-700 border-emerald-300 bg-emerald-50" : "text-amber-700 border-amber-300 bg-amber-50"
                    }`}
                    data-testid={`chooser-availability-${c.id}`}
                  >
                    {available ? t("chooser.available") : t("chooser.comingSoon")}
                  </span>
                </div>
                <p className="text-lg font-semibold text-slate-900 leading-snug mb-2">{c.q}</p>
                <p className="text-sm text-slate-600 leading-relaxed mb-6">{c.desc}</p>
                <div className="flex items-center justify-between border-t border-slate-300 pt-4">
                  <span className="text-[11px] font-bold tracking-[0.22em] text-blue-600 uppercase mono">{c.service}</span>
                  <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                </div>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Hero Video (1:1, autoplay-muted-loop, with mute toggle)
// ============================================================
function HeroVideo({ src }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    const newMuted = !muted;
    v.muted = newMuted;
    setMuted(newMuted);
    if (!newMuted) v.play().catch(() => {});
  };

  return (
    <div className="relative aspect-square mx-auto rounded-2xl overflow-hidden bg-[#0F0F17] border border-slate-300 group">
      <video
        ref={videoRef}
        src={src}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        className="w-full h-full object-cover object-top"
        data-testid="hero-video"
      />
      <button
        type="button"
        onClick={toggleMute}
        aria-label={muted ? "Activar audio" : "Silenciar"}
        data-testid="hero-video-mute"
        className="absolute bottom-4 right-4 z-10 w-10 h-10 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white hover:bg-black/80 hover:scale-110 transition-all"
      >
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>
      <div className="absolute inset-0 rounded-2xl pointer-events-none ring-1 ring-inset ring-white/5" />
    </div>
  );
}

// ============================================================
// Flag Icon (small inline SVG for ES / EN badges)
// ============================================================
function FlagIcon({ code }) {
  const c = (code || "").toUpperCase();
  if (c === "ES") {
    return (
      <svg width="14" height="10" viewBox="0 0 6 4" aria-hidden="true" className="rounded-[1px] shrink-0">
        <rect width="6" height="4" fill="#C60B1E" />
        <rect y="1" width="6" height="2" fill="#FFC400" />
      </svg>
    );
  }
  if (c === "EN" || c === "GB" || c === "UK") {
    return (
      <svg width="14" height="10" viewBox="0 0 60 36" aria-hidden="true" className="rounded-[1px] shrink-0">
        <rect width="60" height="36" fill="#012169" />
        <path d="M0,0 L60,36 M60,0 L0,36" stroke="#FFFFFF" strokeWidth="6" />
        <path d="M0,0 L60,36 M60,0 L0,36" stroke="#C8102E" strokeWidth="3" />
        <path d="M30,0 V36 M0,18 H60" stroke="#FFFFFF" strokeWidth="10" />
        <path d="M30,0 V36 M0,18 H60" stroke="#C8102E" strokeWidth="6" />
      </svg>
    );
  }
  return null;
}

// ============================================================
// Video Placeholder
// ============================================================
function VideoPlaceholder({ caption, label, langBadge, compact }) {
  const { language } = useLanguage();
  const placeholderText = language === "es" ? "Vídeo explicativo próximamente" : "Explainer video coming soon";
  const upperLabel = language === "es" ? "Vídeo explicativo — 90 segundos" : "Explainer — 90 seconds";

  return (
    <div className={compact ? "" : "my-8"}>
      <div className="flex items-center justify-between mb-3 gap-2">
        <span className="text-[10px] font-bold tracking-[0.22em] text-blue-600 uppercase mono truncate">
          {label || upperLabel}
        </span>
        <span className="text-[10px] font-medium text-slate-600 uppercase tracking-[0.18em] mono shrink-0">
          MP4 · 9:16
        </span>
      </div>
      <div className="relative aspect-[9/16] max-w-[280px] sm:max-w-[300px] mx-auto rounded-2xl overflow-hidden video-placeholder-bg border border-slate-300">
        {langBadge && (
          <span
            data-testid={`video-lang-badge-${langBadge.toLowerCase()}`}
            className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.18em] text-slate-900 bg-blue-500/90 px-2 py-1 rounded-md mono"
          >
            <FlagIcon code={langBadge} />
            {langBadge}
          </span>
        )}
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-4 text-center">
          <div className="relative">
            <div className="w-16 h-16 rounded-full bg-blue-50 backdrop-blur-md border border-blue-400/30 flex items-center justify-center pulse-accent">
              <Play className="w-6 h-6 text-blue-700 fill-blue-300/30 ml-1" />
            </div>
          </div>
          <p className="text-sm font-medium text-slate-700 mono tracking-wide">{placeholderText}</p>
        </div>
        {/* Glow border */}
        <div className="absolute inset-0 rounded-2xl pointer-events-none ring-1 ring-inset ring-white/5" />
      </div>
      {caption && <p className="mt-3 text-sm text-slate-500 italic text-center">{caption}</p>}
    </div>
  );
}

// ============================================================
// Service Video (real video, 9:16, with lang badge + tap to unmute)
// ============================================================
function ServiceVideo({ src, caption, langBadge, compact }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const { language } = useLanguage();
  const upperLabel = language === "es" ? "Vídeo explicativo" : "Explainer video";

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    const newMuted = !muted;
    v.muted = newMuted;
    setMuted(newMuted);
    if (!newMuted) {
      v.play().catch(() => {});
    }
  };

  return (
    <div className={compact ? "" : "my-8"}>
      <div className="flex items-center justify-between mb-3 gap-2">
        <span className="text-[10px] font-bold tracking-[0.22em] text-blue-600 uppercase mono truncate">
          {upperLabel}
        </span>
        <span className="text-[10px] font-medium text-slate-600 uppercase tracking-[0.18em] mono shrink-0">
          MP4 · 9:16
        </span>
      </div>
      <div className="relative aspect-[9/16] max-w-[280px] sm:max-w-[300px] mx-auto rounded-2xl overflow-hidden bg-[#0F0F17] border border-slate-300 group">
        <video
          ref={videoRef}
          src={src}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          className="w-full h-full object-cover"
          data-testid={`service-video-${langBadge ? langBadge.toLowerCase() : "single"}`}
        />
        {langBadge && (
          <span
            data-testid={`video-lang-badge-${langBadge.toLowerCase()}`}
            className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.18em] text-slate-900 bg-blue-500/90 px-2 py-1 rounded-md mono"
          >
            <FlagIcon code={langBadge} />
            {langBadge}
          </span>
        )}
        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? "Activar audio" : "Silenciar"}
          data-testid={`service-video-mute-${langBadge ? langBadge.toLowerCase() : "single"}`}
          className="absolute bottom-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white hover:bg-black/80 hover:scale-110 transition-all"
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
        <div className="absolute inset-0 rounded-2xl pointer-events-none ring-1 ring-inset ring-white/5" />
      </div>
      {caption && <p className="mt-3 text-sm text-slate-500 italic text-center">{caption}</p>}
    </div>
  );
}

// ============================================================
// Service section (reusable)
// ============================================================
function Service({ id, serviceKey, num, tag, title, subtitle, definition, problemTitle, problems, includesTitle, includes, excludesTitle, excludes, forTitle, forText, modelTitle, modelText, anchor, cta1, cta2, whatsappMsg, videoCaption, reverseLayout, finalCta, videoUrls, comingSoon, available, steps, showScope }) {
  const { language, t } = useLanguage();
  const sectionRef = useRef(null);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        track.serviceViewed(serviceKey);
        obs.disconnect();
      }
    }, { threshold: 0.35 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [serviceKey]);
  const onCta = (e) => {
    e.preventDefault();
    selectService(serviceKey, { source: "service" });
  };
  // Pick video matching current language; fallback to the other if not available
  const activeVideoSrc = videoUrls
    ? (videoUrls[language] || videoUrls.en || videoUrls.es || null)
    : null;
  const activeLangBadge = videoUrls
    ? (videoUrls[language] ? language.toUpperCase() : (videoUrls.en ? "EN" : (videoUrls.es ? "ES" : null)))
    : null;
  const comingSoonLabel = language === "es" ? "Próximamente · registro de interés" : "Coming soon · interest registration";
  const availableLabel = language === "es" ? "Disponible ahora" : "Available now";
  return (
    <section id={id} ref={sectionRef} className="py-24 lg:py-32 relative border-t border-slate-300">
      <div className="absolute inset-0 mesh-soft pointer-events-none opacity-40" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className={`grid lg:grid-cols-12 gap-10 lg:gap-14 ${reverseLayout ? "lg:[&>*:first-child]:order-2" : ""}`}>
          {/* Sticky title side */}
          <motion.div {...fadeUp} className="lg:col-span-5">
            <div className="lg:sticky lg:top-32 space-y-6">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-6xl font-semibold text-slate-900/[0.06] mono tracking-tighter leading-none">
                  {num}
                </span>
                <span className="text-[11px] font-bold tracking-[0.22em] text-blue-600 uppercase mono">
                  {tag}
                </span>
              </div>
              {comingSoon && (
                <div
                  data-testid={`${id}-coming-soon-badge`}
                  className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 text-black text-sm sm:text-base font-extrabold uppercase tracking-[0.16em] shadow-[0_8px_32px_rgba(251,191,36,0.55)] ring-2 ring-amber-300/40 animate-[pulse_2.4s_ease-in-out_infinite]"
                >
                  <span className="relative flex h-3 w-3">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-80 animate-ping" />
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-red-600 ring-2 ring-amber-200" />
                  </span>
                  {comingSoonLabel}
                </div>
              )}
              {available && (
                <div
                  data-testid={`${id}-available-badge`}
                  className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-gradient-to-r from-emerald-400 via-green-400 to-emerald-400 text-black text-sm sm:text-base font-extrabold uppercase tracking-[0.16em] shadow-[0_8px_32px_rgba(16,185,129,0.55)] ring-2 ring-emerald-300/40"
                >
                  <span className="relative flex h-3 w-3">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-700 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-800 ring-2 ring-emerald-200" />
                  </span>
                  {availableLabel}
                </div>
              )}
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.08] text-gradient">
                {title}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">{subtitle}</p>
              <blockquote className="relative pl-5 border-l-2 border-blue-500/60 italic text-slate-800 text-base">
                <span className="absolute -left-0.5 top-0 bottom-0 w-px bg-gradient-to-b from-blue-400 via-blue-500 to-transparent" />
                "{anchor}"
              </blockquote>
              <div className="flex flex-wrap gap-3 pt-2">
                <a href="#contacto" data-testid={`${id}-cta1`} onClick={onCta}>
                  <Button className="bg-blue-600 hover:bg-blue-500 text-white rounded-lg px-6 h-12 font-semibold gap-2 transition-all shadow-lg shadow-blue-600/25 hover:shadow-blue-500/40 active:scale-[0.98]">
                    {cta1}
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </a>
                <a href={getWA(whatsappMsg)} target="_blank" rel="noopener noreferrer" data-testid={`${id}-whatsapp`}>
                  <Button className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-lg px-6 h-12 font-semibold gap-2">
                    <WhatsAppIcon className="w-4 h-4" />
                    {cta2}
                  </Button>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Content side */}
          <div className="lg:col-span-7 space-y-8">
            <motion.div {...fadeUp}>
              <p className="text-lg text-slate-800 leading-relaxed border-l border-slate-300 pl-5">
                {definition}
              </p>
            </motion.div>

            {showScope && (
              <motion.div {...fadeUp} className="grid sm:grid-cols-2 gap-3" data-testid={`${id}-scope`}>
                <div className="flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
                  <Cpu className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-800 leading-snug font-medium">{t("services.techLabel")}</p>
                </div>
                <div className="flex gap-3 rounded-xl border border-slate-300 bg-slate-50 p-4">
                  <Layers className="w-5 h-5 text-slate-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-700 leading-snug">{t("services.otherLabel")}</p>
                </div>
              </motion.div>
            )}

            {steps && (
              <motion.div {...fadeUp} data-testid={`${id}-steps`}>
                <h3 className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-4 mono">
                  {t("services.stepsTitle")}
                </h3>
                <ol className="grid sm:grid-cols-2 gap-3">
                  {steps.map((s, i) => (
                    <li key={i} className="flex gap-3 rounded-xl glass border border-slate-300 p-4">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-700 text-xs font-bold mono">{i + 1}</span>
                      <span className="text-sm text-slate-800 leading-snug pt-1">{s}</span>
                    </li>
                  ))}
                </ol>
              </motion.div>
            )}

            {/* Video — single, language-aware */}
            <motion.div {...fadeUp}>
              {activeVideoSrc ? (
                <ServiceVideo
                  key={activeVideoSrc}
                  src={activeVideoSrc}
                  caption={videoCaption}
                  langBadge={activeLangBadge}
                />
              ) : (
                <VideoPlaceholder caption={videoCaption} />
              )}
            </motion.div>

            <motion.div {...fadeUp}>
              <h3 className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-3 mono">
                {problemTitle}
              </h3>
              <ul className="space-y-2.5">
                {problems.map((p, i) => (
                  <li key={i} className="flex gap-3 text-[15px] text-slate-700">
                    <span className="text-blue-600 font-bold mt-0.5 mono">—</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Bento Grid: includes / excludes */}
            <div className="grid sm:grid-cols-2 gap-4">
              <motion.div {...fadeUp} className="card-gradient-border rounded-2xl p-6">
                <h3 className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-600 mb-4 flex items-center gap-1.5 mono">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {includesTitle}
                </h3>
                <ul className="space-y-2">
                  {includes.map((p, i) => (
                    <li key={i} className="text-sm text-slate-700 leading-snug flex gap-2">
                      <span className="text-emerald-600 mt-1">·</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div {...fadeUp} className="card-gradient-border rounded-2xl p-6">
                <h3 className="text-[10px] font-bold uppercase tracking-[0.22em] text-red-600 mb-4 flex items-center gap-1.5 mono">
                  <XCircle className="w-3.5 h-3.5" />
                  {excludesTitle}
                </h3>
                <ul className="space-y-2">
                  {excludes.map((p, i) => (
                    <li key={i} className="text-sm text-slate-700 leading-snug flex gap-2">
                      <span className="text-red-500 mt-1">·</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            <motion.div {...fadeUp} className="grid sm:grid-cols-2 gap-6 pt-2">
              <div>
                <h3 className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-2 mono">
                  {forTitle}
                </h3>
                <p className="text-sm text-slate-700 leading-relaxed">{forText}</p>
              </div>
              <div>
                <h3 className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-2 mono">
                  {modelTitle}
                </h3>
                <p className="text-sm text-slate-700 leading-relaxed">{modelText}</p>
              </div>
            </motion.div>

            {/* Contextual final CTA */}
            <motion.div
              {...fadeUp}
              className="relative overflow-hidden rounded-2xl p-7 glass-strong"
            >
              <div className="absolute -top-1/2 -right-1/4 w-72 h-72 bg-blue-50 rounded-full blur-3xl" />
              <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <p className="text-lg font-medium text-slate-900">{finalCta}</p>
                <a href="#contacto" data-testid={`${id}-final-cta`} onClick={onCta}>
                  <Button className="bg-blue-600 hover:bg-blue-500 text-white rounded-lg px-6 h-11 font-medium gap-2 whitespace-nowrap shadow-lg shadow-blue-600/25">
                    {cta1}
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Services container
// ============================================================
function ServicesSection() {
  const { t } = useLanguage();
  return (
    <>
      <section id="servicios" className="pt-24 lg:pt-32 pb-4 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-3xl">
            <span className="text-[11px] font-bold tracking-[0.22em] text-blue-600 uppercase mono mb-3 block">
              {t("services.title")}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4 tracking-[-0.03em] leading-[1.08] text-gradient">
              {t("services.heading")}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl">{t("services.subtitle")}</p>
          </motion.div>
        </div>
      </section>

      <Service
        id="servicio-import"
        serviceKey="import"
        num="01"
        steps={t("services.importSteps")}
        showScope
        tag={t("services.importTag")}
        title={t("services.importTitle")}
        subtitle={t("services.importSubtitle")}
        definition={t("services.importDefinition")}
        problemTitle={t("services.importProblemTitle")}
        problems={t("services.importProblem")}
        includesTitle={t("services.importIncludesTitle")}
        includes={t("services.importIncludes")}
        excludesTitle={t("services.importExcludesTitle")}
        excludes={t("services.importExcludes")}
        forTitle={t("services.importForTitle")}
        forText={t("services.importFor")}
        modelTitle={t("services.importModelTitle")}
        modelText={t("services.importModel")}
        anchor={t("services.importAnchor")}
        cta1={t("services.importCta1")}
        cta2={t("services.importCta2")}
        whatsappMsg={t("services.importWhatsapp")}
        videoCaption={t("services.importVideoCaption")}
        finalCta={t("services.importFinalCta")}
        videoUrls={SERVICE_VIDEOS.import}
        available
      />

      <Service
        id="servicio-europe-entry"
        serviceKey="europe"
        num="02"
        steps={t("services.europeSteps")}
        tag={t("services.europeTag")}
        title={t("services.europeTitle")}
        subtitle={t("services.europeSubtitle")}
        definition={t("services.europeDefinition")}
        problemTitle={t("services.europeProblemTitle")}
        problems={t("services.europeProblem")}
        includesTitle={t("services.europeIncludesTitle")}
        includes={t("services.europeIncludes")}
        excludesTitle={t("services.europeExcludesTitle")}
        excludes={t("services.europeExcludes")}
        forTitle={t("services.europeForTitle")}
        forText={t("services.europeFor")}
        modelTitle={t("services.europeModelTitle")}
        modelText={t("services.europeModel")}
        anchor={t("services.europeAnchor")}
        cta1={t("services.europeCta1")}
        cta2={t("services.europeCta2")}
        whatsappMsg={t("services.europeWhatsapp")}
        videoCaption={t("services.europeVideoCaption")}
        finalCta={t("services.europeFinalCta")}
        reverseLayout
        videoUrls={SERVICE_VIDEOS.europe}
        available
      />

      <Service
        id="servicio-buy"
        serviceKey="buy"
        num="03"
        steps={t("services.buySteps")}
        tag={t("services.buyTag")}
        title={t("services.buyTitle")}
        subtitle={t("services.buySubtitle")}
        definition={t("services.buyDefinition")}
        problemTitle={t("services.buyProblemTitle")}
        problems={t("services.buyProblem")}
        includesTitle={t("services.buyIncludesTitle")}
        includes={t("services.buyIncludes")}
        excludesTitle={t("services.buyExcludesTitle")}
        excludes={t("services.buyExcludes")}
        forTitle={t("services.buyForTitle")}
        forText={t("services.buyFor")}
        modelTitle={t("services.buyModelTitle")}
        modelText={t("services.buyModel")}
        anchor={t("services.buyAnchor")}
        cta1={t("services.buyCta1")}
        cta2={t("services.buyCta2")}
        whatsappMsg={t("services.buyWhatsapp")}
        videoCaption={t("services.buyVideoCaption")}
        finalCta={t("services.buyFinalCta")}
        videoUrls={SERVICE_VIDEOS.buy}
        comingSoon
      />
    </>
  );
}

// ============================================================
// Compare table
// ============================================================
function CompareSection() {
  const { t } = useLanguage();
  const headers = t("compare.headers");
  const rows = t("compare.rows");

  return (
    <section id="comparativa" className="py-24 lg:py-32 relative section-band">
      <div className="absolute inset-0 mesh-soft opacity-50 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div {...fadeUp} className="max-w-3xl mb-14">
          <span className="text-[11px] font-bold tracking-[0.22em] text-blue-600 uppercase mono mb-3 block">
            {t("nav.compare")}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4 tracking-[-0.03em] leading-[1.08] text-gradient">
            {t("compare.title")}
          </h2>
          <p className="text-lg text-slate-600">{t("compare.subtitle")}</p>
        </motion.div>

        <motion.div {...fadeUp} className="relative rounded-xl border border-slate-300 border-t-4 border-t-blue-600 bg-white overflow-x-auto shadow-[0_10px_30px_-18px_rgba(15,23,42,0.25)]">
          <table className="w-full min-w-[720px] border-collapse">
            <thead>
              <tr className="bg-slate-100 border-b border-slate-300">
                {headers.map((h, i) => (
                  <th
                    key={i}
                    className={`text-left py-4 px-5 text-[11px] font-semibold uppercase tracking-[0.18em] mono ${
                      i === 0 ? "text-slate-500" : "text-blue-600"
                    }`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="border-b border-slate-300 last:border-b-0 hover:bg-slate-50 transition-colors">
                  <td className="py-5 px-5 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mono">
                    {row.label}
                  </td>
                  {row.values.map((v, j) => (
                    <td key={j} className="py-5 px-5 text-sm text-slate-800">
                      {v}
                    </td>
                  ))}
                </tr>
              ))}
              <tr>
                <td className="py-5 px-5" />
                {["import", "europe", "buy"].map((key, j) => (
                  <td key={key} className="py-5 px-5">
                    <a href="#contacto" onClick={(e) => { e.preventDefault(); selectService(key, { source: "compare" }); }} data-testid={`compare-cta-${key}`}>
                      <Button size="sm" className={`rounded-md h-10 px-4 text-xs font-semibold gap-1.5 ${key === "buy" ? "bg-slate-100 hover:bg-slate-200 text-slate-900 border border-slate-300" : "bg-blue-600 hover:bg-blue-500 text-white"}`}>
                        {t("compare.ctas")[j]}
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Button>
                    </a>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </motion.div>
        <p className="mt-5 text-xs sm:text-sm text-slate-500 flex items-start gap-2 max-w-3xl" data-testid="compare-note">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          {t("compare.note")}
        </p>
      </div>
    </section>
  );
}

// ============================================================
// Differentiation (Bento)
// ============================================================
function DiffSection() {
  const { t } = useLanguage();
  const items = t("diff.items");

  return (
    <section id="diferenciacion" className="py-24 lg:py-32 section-alt border-t border-slate-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp} className="max-w-3xl mb-14">
          <span className="text-[11px] font-bold tracking-[0.22em] text-blue-600 uppercase mono mb-3 block">
            {t("nav.diff")}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4 tracking-[-0.03em] leading-[1.08] text-gradient">
            {t("diff.title")}
          </h2>
          <p className="text-lg text-slate-600">{t("diff.subtitle")}</p>
        </motion.div>

        {/* Bento layout: first item large */}
        <div className="grid md:grid-cols-3 gap-4 lg:gap-5">
          {items.map((it, i) => {
            const featured = i === 0;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className={`relative card-gradient-border rounded-2xl p-7 overflow-hidden ${
                  featured ? "md:col-span-3 lg:col-span-1 lg:row-span-2 lg:p-9" : ""
                }`}
              >
                {featured && (
                  <>
                    <div className="absolute -top-20 -right-20 w-72 h-72 bg-blue-50 rounded-full blur-3xl" />
                    <div className="absolute -bottom-32 -left-20 w-64 h-64 bg-indigo-500/15 rounded-full blur-3xl" />
                  </>
                )}
                <div className="relative">
                  <div className={`mono font-extrabold mb-4 ${featured ? "text-5xl text-blue-600" : "text-3xl text-slate-300"}`}>
                    0{i + 1}
                  </div>
                  <h3 className={`font-bold text-slate-900 mb-2 ${featured ? "text-2xl" : "text-lg"}`}>
                    {it.title}
                  </h3>
                  <p className={`text-slate-600 leading-relaxed ${featured ? "text-base" : "text-sm"}`}>
                    {it.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// About GTX
// ============================================================
function AboutSection() {
  const { t } = useLanguage();
  const proofTitles = [t("about.proofVideo1"), t("about.proofVideo2"), t("about.proofVideo3")];

  const stats = [
    { title: t("about.operationTitle"), value: t("about.operationValue"), icon: Globe },
    { title: t("about.brandsTitle"), value: t("about.brandsValue"), icon: Boxes },
    { title: t("about.suppliersTitle"), value: t("about.suppliersValue"), icon: Package },
    { title: t("about.marketsTitle"), value: t("about.marketsValue"), icon: Building2 },
  ];

  return (
    <section id="sobre-gtx" className="py-24 lg:py-32 relative section-band">
      <div className="absolute inset-0 mesh-soft opacity-50 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 mb-16">
          <motion.div {...fadeUp} className="lg:col-span-6">
            <span className="text-[11px] font-bold tracking-[0.22em] text-blue-600 uppercase mono mb-3 block">
              {t("about.tag")}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold mb-5 tracking-[-0.03em] leading-[1.08] text-gradient">
              {t("about.title")}
            </h2>
            <p className="text-lg text-slate-700 mb-5 leading-relaxed">{t("about.subtitle")}</p>
            <p className="text-base text-slate-600 leading-relaxed">{t("about.paragraph1")}</p>
          </motion.div>

          <motion.div {...fadeUp} className="lg:col-span-6 grid sm:grid-cols-2 gap-4">
            {stats.map((s, i) => (
              <div key={i} className="card-gradient-border rounded-2xl p-5">
                <s.icon className="w-5 h-5 text-blue-600 mb-3" strokeWidth={1.5} />
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-2 mono">
                  {s.title}
                </div>
                <div className="text-sm text-slate-900 font-medium leading-snug">{s.value}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Operational proof */}
        <motion.div {...fadeUp} className="mb-8">
          <h3 className="text-2xl font-bold text-slate-900 mb-1">{t("about.proofTitle")}</h3>
          <p className="text-sm text-slate-600">{t("about.proofSubtitle")}</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-5 mb-14">
          {STOCK_VIDEOS.map((url, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="relative overflow-hidden rounded-2xl bg-[#0F0F17] aspect-[9/16] group border border-slate-300"
            >
              <video
                src={url}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent pointer-events-none" />
              <div className="absolute top-4 left-4">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full glass text-[10px] font-bold text-slate-900 uppercase tracking-[0.18em] mono">
                  <span className="w-1 h-1 rounded-full bg-red-500 animate-pulse" />
                  Stock real
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <p className="text-base font-semibold text-slate-900 leading-tight">{proofTitles[i]}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* What GTX is NOT */}
        <motion.div {...fadeUp} className="relative glass-strong rounded-2xl p-8 lg:p-10 overflow-hidden">
          <div className="absolute -top-32 -right-32 w-80 h-80 bg-blue-50 rounded-full blur-3xl" />
          <h3 className="relative text-[10px] font-bold uppercase tracking-[0.22em] text-blue-600 mb-6 mono">
            {t("about.notTitle")}
          </h3>
          <ul className="relative grid sm:grid-cols-2 gap-4">
            {t("about.notItems").map((item, i) => (
              <li key={i} className="flex gap-3 text-slate-700 text-base">
                <X className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
}

// ============================================================
// FAQ
// ============================================================
function FAQSection() {
  const { t } = useLanguage();
  const items = t("faq.items");

  return (
    <section id="faq" className="py-24 lg:py-32 section-band">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp} className="mb-12">
          <span className="text-[11px] font-bold tracking-[0.22em] text-blue-600 uppercase mono mb-3 block">
            FAQ
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.08] text-gradient">
            {t("faq.title")}
          </h2>
        </motion.div>

        <Accordion type="single" collapsible className="space-y-2" data-testid="faq-accordion">
          {items.map((it, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="card-gradient-border rounded-xl px-6 border data-[state=open]:border-blue-300 data-[state=open]:bg-blue-50/40"
            >
              <AccordionTrigger
                className="text-slate-900 hover:text-blue-600 hover:no-underline py-5 font-medium text-left text-[15px] transition-colors [&[data-state=open]>svg]:rotate-180"
                data-testid={`faq-trigger-${i}`}
              >
                {it.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 pb-5 text-[15px] leading-relaxed">
                {it.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

// ============================================================
// Contact form
// ============================================================
function ContactSection() {
  const { t } = useLanguage();

  return (
    <section id="contacto" className="py-24 lg:py-32 relative section-alt border-t border-slate-300">
      <div className="absolute inset-0 mesh-hero opacity-30 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-10">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <span className="text-[11px] font-bold tracking-[0.22em] text-blue-600 uppercase mono mb-3 block">
              {t("contact.tag")}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4 tracking-[-0.03em] leading-[1.08] text-gradient">
              {t("contact.title")}
            </h2>
            <p className="text-base md:text-lg text-slate-600 mb-8 leading-relaxed">{t("contact.subtitle")}</p>

            <div className="space-y-3 mb-6">
              <a
                href={getWA(t("whatsappHome"))}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 card-gradient-border rounded-xl"
                data-testid="contact-direct-whatsapp"
              >
                <div className="w-10 h-10 rounded-lg bg-[#25D366]/15 flex items-center justify-center">
                  <WhatsAppIcon className="w-5 h-5 text-[#25D366]" />
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mono">WhatsApp</div>
                  <div className="text-sm font-semibold text-slate-900">+31 645 282 452</div>
                </div>
              </a>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="flex items-center gap-3 p-4 card-gradient-border rounded-xl"
                data-testid="contact-direct-email"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mono">Email</div>
                  <div className="text-sm font-semibold text-slate-900">{CONTACT_EMAIL}</div>
                </div>
              </a>
            </div>

            <p className="text-xs text-slate-500 italic">{t("contact.whatsappLine")}</p>
          </motion.div>

          <motion.div {...fadeUp} className="lg:col-span-7 relative">
            <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-blue-500/20 via-transparent to-transparent blur-xl opacity-60" />
            <div className="relative glass-strong rounded-2xl p-6 sm:p-7 lg:p-9" style={{ borderTopWidth: 4, borderTopColor: "#2563EB" }}>
              <LeadForm />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Footer
// ============================================================
function Footer() {
  const { t, language } = useLanguage();
  const year = new Date().getFullYear();

  return (
    <footer className="relative bg-white border-t border-slate-300 py-16">
      <div className="absolute inset-0 mesh-soft opacity-30 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid md:grid-cols-12 gap-8 mb-12">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 rounded-full bg-white p-[2px] ring-2 ring-blue-500/40 overflow-hidden shadow-[0_6px_28px_-6px_rgba(59,130,246,0.5)]">
                <img src={LOGO_URL} alt="Global Tech Xperts" className="w-full h-full object-cover rounded-full scale-[1.12]" />
              </div>
              <div className="font-bold text-lg text-slate-900">Global Tech Xperts</div>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed max-w-md mb-5">{t("footer.tagline")}</p>
            <a href={getWA(t("whatsappHome"))} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-[#25D366] hover:text-[#1ebe57]">
              <WhatsAppIcon className="w-4 h-4" />
              +31 645 282 452
            </a>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-[10px] font-bold uppercase tracking-[0.22em] text-blue-600 mb-4 mono">{t("footer.services")}</h4>
            <ul className="space-y-2.5 text-sm text-slate-600">
              <li><a href="#servicio-import" className="hover:text-slate-900 transition-colors">GTX Import</a></li>
              <li><a href="#servicio-europe-entry" className="hover:text-slate-900 transition-colors">GTX Europe Entry</a></li>
              <li><a href="#servicio-buy" className="hover:text-slate-900 transition-colors">GTX Buy For You</a></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-[10px] font-bold uppercase tracking-[0.22em] text-blue-600 mb-4 mono">{t("footer.company")}</h4>
            <ul className="space-y-2.5 text-sm text-slate-600">
              <li><a href="#sobre-gtx" className="hover:text-slate-900 transition-colors">{t("nav.about")}</a></li>
              <li><a href="#comparativa" className="hover:text-slate-900 transition-colors">{t("nav.compare")}</a></li>
              <li><a href="#contacto" className="hover:text-slate-900 transition-colors">{t("nav.contact")}</a></li>
            </ul>
          </div>

          {STARLINK_ENABLED && (
            <div className="md:col-span-2">
              <h4 className="text-[10px] font-bold uppercase tracking-[0.22em] text-blue-600 mb-4 mono">Starlink Mini</h4>
              <ul className="space-y-2.5 text-sm text-slate-600">
                <li><Link to="/reserva-starlink-mini" className="hover:text-slate-900 transition-colors" data-testid="footer-starlink-link">
                  {language === "es" ? "Reservar plaza" : "Reserve a spot"}
                </Link></li>
                <li><Link to="/mi-reserva" className="hover:text-blue-700 transition-colors font-semibold text-blue-600" data-testid="footer-tracking-link">
                  {language === "es" ? "Seguir mi pedido →" : "Track my order →"}
                </Link></li>
              </ul>
            </div>
          )}

          <div className="md:col-span-2">
            <h4 className="text-[10px] font-bold uppercase tracking-[0.22em] text-blue-600 mb-4 mono">{t("footer.legal")}</h4>
            <ul className="space-y-2.5 text-sm text-slate-600">
              <li><Link to="/legal" className="hover:text-slate-900 transition-colors" data-testid="footer-legal-link">{t("footer.legalNotice")}</Link></li>
              <li><Link to="/privacy" className="hover:text-slate-900 transition-colors" data-testid="footer-privacy-link">{t("footer.privacy")}</Link></li>
              <li><Link to="/cookies" className="hover:text-slate-900 transition-colors" data-testid="footer-cookies-link">{t("footer.cookies")}</Link></li>
            </ul>
          </div>
        </div>

        <div className="pt-6 border-t border-slate-300 flex flex-col md:flex-row justify-between gap-4 text-xs text-slate-500 mono">
          <p>© {year} Global Tech Xperts (GTX). {t("footer.copyright")}</p>
          <div className="flex items-center gap-4 flex-wrap">
            <Link to="/crm/login" className="hover:text-slate-900 transition-colors" data-testid="footer-crm-link">
              {language === "es" ? "Acceso agentes" : "Agents access"}
            </Link>
            {STARLINK_ENABLED && (
              <Link to="/mi-reserva" className="hover:text-blue-700 transition-colors" data-testid="footer-mireserva-link">
                {language === "es" ? "Mi reserva Starlink" : "My Starlink order"}
              </Link>
            )}
            <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-slate-900 transition-colors">{CONTACT_EMAIL}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ============================================================
// Header WhatsApp (section-aware message, official logo)
// ============================================================
function HeaderWhatsApp() {
  const { t, language } = useLanguage();
  const [section, setSection] = useState("home");

  useEffect(() => {
    const ids = ["servicio-import", "servicio-europe-entry", "servicio-buy", "sobre-gtx", "contacto"];
    const onScroll = () => {
      let active = "home";
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight / 2 && r.bottom > window.innerHeight / 2) active = id;
      }
      setSection(active);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const messageMap = {
    "servicio-import": t("services.importWhatsapp"),
    "servicio-buy": t("services.buyWhatsapp"),
    "servicio-europe-entry": t("services.europeWhatsapp"),
  };
  const msg = messageMap[section] || t("whatsappHome");
  const label = language === "es" ? "Habla con GTX" : "Chat with GTX";

  return (
    <a
      href={getWA(msg)}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 h-9 px-2.5 sm:px-3.5 bg-[#25D366] hover:bg-[#22C55E] text-slate-950 rounded-lg text-sm font-semibold transition-[background-color,box-shadow,transform] shadow-md shadow-emerald-900/20 hover:-translate-y-px"
      data-testid="header-whatsapp-btn"
      aria-label="WhatsApp"
      title="WhatsApp"
    >
      <WhatsAppIcon className="w-[18px] h-[18px]" />
      <span className="hidden md:inline">{label}</span>
    </a>
  );
}

// ============================================================
// Back to top
// ============================================================
function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!show) return null;
  return (
    <a
      href="#top"
      className="fixed bottom-5 left-5 z-50 hidden md:flex items-center justify-center w-11 h-11 rounded-full glass-strong text-slate-900 hover:bg-slate-100 transition-colors"
      data-testid="back-to-top-btn"
    >
      <ArrowUp className="w-5 h-5" />
    </a>
  );
}

// ============================================================
// Main
// ============================================================
export default function LandingPage() {
  const { t, language } = useLanguage();
  useEffect(() => {
    const preset = captureCampaignParams();
    if (preset) setTimeout(() => selectService(preset, { scroll: false, source: "url" }), 0);
  }, []);
  useEffect(() => {
    document.title = t("seo.title");
  }, [t]);
  return (
    <div className="theme-light min-h-screen bg-[#F6F7FA] text-slate-900">
      <Helmet>
        <html lang={language} />
        <meta name="description" content={t("seo.description")} />
        <meta property="og:title" content={t("seo.title")} />
        <meta property="og:description" content={t("seo.description")} />
      </Helmet>
      {STARLINK_ENABLED && <StarlinkTopBanner />}
      <Header />
      <main>
        <Hero />
        {STARLINK_ENABLED && <StarlinkCampaignBlock />}
        <ChooserSection />
        <OperativaStrip />
        <ServicesSection />
        <CompareSection />
        <DiffSection />
        <AboutSection />
        <FAQSection />
        <ContactSection />
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}

/* ============ STARLINK CAMPAIGN — top strip + featured block ============ */
function StarlinkTopBanner() {
  const [config, setConfig] = useState(null);
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    // 7-day dismiss memory
    const dismissedAt = localStorage.getItem("gtx_starlink_banner_dismissed_at");
    if (dismissedAt && Date.now() - Number(dismissedAt) < 7 * 24 * 3600 * 1000) return;
    axios.get(`${API}/starlink/config`).then((r) => {
      if (r.data?.campana_activa) {
        setConfig(r.data);
        setHidden(false);
      }
    }).catch(() => {});
  }, []);

  const dismiss = () => {
    localStorage.setItem("gtx_starlink_banner_dismissed_at", String(Date.now()));
    setHidden(true);
    // adjust header padding removed by setting a body class off
    document.body.classList.remove("has-starlink-banner");
  };

  useEffect(() => {
    if (!hidden) document.body.classList.add("has-starlink-banner");
    else document.body.classList.remove("has-starlink-banner");
    return () => document.body.classList.remove("has-starlink-banner");
  }, [hidden]);

  if (hidden || !config) return null;
  const precio = config.precio_total ?? 399;
  const plazo = config.plazo_entrega || "4-6 semanas";

  return (
    <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-blue-600 to-blue-500 border-b border-blue-400/30" data-testid="starlink-top-banner">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 h-10 flex items-center justify-between gap-3 text-slate-900">
        <div className="flex-1 min-w-0 text-xs sm:text-sm truncate">
          <span className="font-bold">⚡ STARLINK MINI</span>
          <span className="hidden sm:inline"> · {precio}€ · Entrega en {plazo}</span>
          <span className="sm:hidden"> · {precio}€ · Reservar</span>
        </div>
        <Link
          to="/mi-reserva"
          data-testid="starlink-banner-tracking"
          className="hidden sm:inline shrink-0 text-xs font-semibold text-slate-800 hover:text-slate-900"
        >
          Mi reserva
        </Link>
        <Link
          to="/reserva-starlink-mini"
          data-testid="starlink-banner-cta"
          className="shrink-0 text-xs sm:text-sm font-bold underline underline-offset-4 hover:text-blue-100"
        >
          Reservar plaza →
        </Link>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Cerrar"
          data-testid="starlink-banner-close"
          className="shrink-0 w-6 h-6 flex items-center justify-center rounded hover:bg-white/15 text-slate-800 hover:text-slate-900"
        >
          ×
        </button>
      </div>
    </div>
  );
}

function StarlinkCampaignBlock() {
  const { language } = useLanguage();
  const [config, setConfig] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    axios.get(`${API}/starlink/config`).then((r) => {
      if (r.data?.campana_activa) setConfig(r.data);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!config) return null;

  const objetivo = config.objetivo_reservas ?? 100;
  const actuales = config.reservas_actuales ?? 0;
  const pct = Math.min(100, (actuales / objetivo) * 100);
  const precio = config.precio_total ?? 399;

  // Countdown
  let cd = null;
  if (config.fecha_cierre) {
    const diff = new Date(config.fecha_cierre).getTime() - now;
    if (diff > 0) {
      cd = {
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
        urgent: diff < 86400000,
        veryUrgent: diff < 3600000,
      };
    }
  }
  const cdColor = cd?.veryUrgent ? "#EF4444" : "#F5A524";

  return (
    <section
      className="relative py-16 lg:py-20 border-t border-b border-blue-200 bg-gradient-to-b from-transparent via-blue-950/10 to-transparent"
      data-testid="starlink-campaign-block"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="rounded-2xl border border-blue-200 bg-[#0F0F16] overflow-hidden">
          <div className="grid lg:grid-cols-2 gap-0">
            {/* Left */}
            <div className="p-8 lg:p-12 relative">
              <div
                data-testid="starlink-coming-soon-badge"
                className="inline-flex items-center gap-2.5 mb-5 px-4 py-2 rounded-full bg-amber-500/15 text-amber-700 border-2 border-amber-500/60 shadow-[0_0_28px_-6px_rgba(245,165,36,0.65)]"
              >
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-400" />
                </span>
                <span className="text-base lg:text-lg font-black tracking-[0.28em] uppercase mono">
                  {language === "es" ? "Próximamente" : "Coming Soon"}
                </span>
              </div>
              <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-blue-600 mono mb-3">
                CAMPAÑA ABIERTA · ESPAÑA
              </div>
              <h2 className="text-3xl lg:text-5xl font-black tracking-tight leading-[1.05] mb-3">
                Starlink Mini — {precio} €
              </h2>
              <p className="text-slate-700 text-base lg:text-lg mb-6">
                Starlink ya no lo vende en propiedad. Nosotros sí.
              </p>
              <ul className="space-y-2.5 mb-8">
                <li className="flex items-center gap-3 text-sm text-slate-800">
                  <span className="w-1 h-4 bg-blue-500 rounded" />
                  Entrega en {config.plazo_entrega || "4-6 semanas"}
                </li>
                <li className="flex items-center gap-3 text-sm text-slate-800">
                  <span className="w-1 h-4 bg-blue-500 rounded" />
                  Envío gratis a toda España · IVA incluido
                </li>
                <li className="flex items-center gap-3 text-sm text-slate-800">
                  <span className="w-1 h-4 bg-blue-500 rounded" />
                  Si no alcanzamos el objetivo, devolvemos el 100 %
                </li>
              </ul>
              <Link
                to="/reserva-starlink-mini"
                data-testid="starlink-block-cta"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg px-6 py-3.5 text-sm font-bold tracking-wide uppercase transition-all shadow-lg shadow-blue-600/25 hover:shadow-blue-500/40 active:scale-[0.98] hover:-translate-y-0.5"
              >
                Ver condiciones y reservar
                <ChevronRight className="w-4 h-4" />
              </Link>

              {/* Progress in amber */}
              <div className="mt-8 pt-6 border-t border-slate-300">
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-white/50">Reservas conseguidas</span>
                  <span className="text-lg font-black" style={{ color: "#F5A524" }}>
                    {actuales} <span className="text-slate-600 text-sm">/ {objetivo}</span>
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full transition-all duration-700"
                    style={{
                      width: `${pct}%`,
                      background: "linear-gradient(90deg, #F5A524 0%, #F5C34A 100%)",
                    }}
                  />
                </div>

                {/* Mini countdown */}
                {cd && (
                  <div className="mt-5 flex items-center gap-2.5" data-testid="home-countdown">
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/50 mono mr-1">
                      {cd.veryUrgent ? "¡Últimos min!" : cd.urgent ? "Últimas horas" : "Cierra en"}
                    </span>
                    <MiniCd v={cd.d} lbl="D" color={cdColor} />
                    <span className="font-black mono" style={{ color: `${cdColor}77` }}>:</span>
                    <MiniCd v={cd.h} lbl="H" color={cdColor} pad />
                    <span className="font-black mono" style={{ color: `${cdColor}77` }}>:</span>
                    <MiniCd v={cd.m} lbl="M" color={cdColor} pad />
                    <span className="font-black mono" style={{ color: `${cdColor}77` }}>:</span>
                    <MiniCd v={cd.s} lbl="S" color={cdColor} pad />
                  </div>
                )}
              </div>
            </div>

            {/* Right — video thumbnail placeholder */}
            <Link
              to="/reserva-starlink-mini"
              className="relative flex items-center justify-center bg-gradient-to-br from-blue-950/70 to-black min-h-[280px] lg:min-h-full border-t lg:border-t-0 lg:border-l border-blue-200 group cursor-pointer"
            >
              {config.video_hero_url ? (
                <video src={config.video_hero_url} muted playsInline className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity" />
              ) : null}
              <div className="relative z-10 w-20 h-20 rounded-full bg-blue-500/25 border-2 border-blue-400/50 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play className="w-8 h-8 text-slate-900 ml-1" />
              </div>
              <div className="absolute bottom-4 left-4 right-4 z-10">
                <div className="text-white/90 text-xs font-semibold tracking-wide">Míralo en 30 segundos →</div>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}


function MiniCd({ v, lbl, color, pad = false }) {
  const value = pad ? String(v).padStart(2, "0") : String(v);
  return (
    <div className="text-center">
      <div className="text-lg lg:text-xl font-black mono leading-none" style={{ color }}>{value}</div>
      <div className="text-[9px] uppercase tracking-widest text-slate-500 mt-0.5">{lbl}</div>
    </div>
  );
}
